import {
  type PlainDate,
  type PlainDateString,
  parsePlainDate,
} from "@sa-nu/shared-utils/date";

import {
  type NightAvailability,
  resolveAccommodationForRoomType,
} from "./room-type-stay-availability";

// ─── 汎用: 部屋タイプ・拠点・エリアの入れ子構造の型 ─────────────────────────────

type SiteWithRoomTypes<RT> = {
  roomTypesForReservation: RT[];
};

type AreaWithSites<S> = {
  sitesForReservation: S[];
};

/**
 * `selectVisibleAreas` が返す、部屋タイプに `unavailableForRange` を付与した入れ子構造の型。
 */
type AnnotatedArea<
  RT,
  S extends SiteWithRoomTypes<RT>,
  A extends AreaWithSites<S>,
> = Omit<A, "sitesForReservation"> & {
  sitesForReservation: (Omit<S, "roomTypesForReservation"> & {
    roomTypesForReservation: (RT & { unavailableForRange: boolean })[];
  })[];
};

// ─── 日程カバレッジ判定 ──────────────────────────────────────────────────────

/**
 * 日程カバレッジ判定が必要とする部屋タイプの最小シェイプ（構造的に受け、特定の型に依存しない）。
 * - availabilities は未取得（undefined）のことがある（その場合は判定材料がない）。
 * - nights も未取得（undefined）のことがある（日付フィルタ無効時は BE から取得しないため）。
 * - 各夜の night / availableAccommodations も optional（欠ける夜は判定からスキップ）。
 */
export type CoverageRoomType = {
  availabilities?: {
    nights?: {
      night?: PlainDateString;
      availableAccommodations?: { id: string }[];
    }[];
  };
};

/**
 * 指定の部屋タイプが選択範囲（firstNight..lastNight）の全泊を1室でカバーできるか判定する。
 * - availabilities が undefined → true（判定材料がないので除外しない）
 * - nights が undefined → true（日付フィルタ有効時は必ず取得されるため型整合のためのフォールバック。
 *   availabilities が undefined のときと同じ扱い）
 * - night が undefined の夜はスキップ（積集合に含めない）
 * - 全泊を通せる1室がなければ false
 */
export function keepRoomTypeForRange(
  rt: CoverageRoomType,
  firstNight: PlainDate,
  lastNight: PlainDate,
): boolean {
  if (
    rt.availabilities === undefined ||
    rt.availabilities.nights === undefined
  ) {
    return true;
  }

  const nights: NightAvailability[] = rt.availabilities.nights
    .filter((n): n is typeof n & { night: string } => n.night !== undefined)
    .map((n) => ({
      night: parsePlainDate(n.night),
      availableAccommodationIds: (n.availableAccommodations ?? []).map(
        (a) => a.id,
      ),
    }));

  return (
    resolveAccommodationForRoomType({ nights, firstNight, lastNight }) !== null
  );
}

// ─── ブラウズ一覧の表示エリア確定（公開 API） ───────────────────────────────

/**
 * ブラウズ一覧に表示するエリア/拠点/部屋タイプを1パスで確定する。
 *
 * フィルタ軸ごとの扱いが異なる:
 * - エリア絞り込み（`selectedAreaSlugs`）: 該当しない部屋タイプは**除外**し、部屋タイプが 0 件に
 *   なった拠点、拠点が 0 件になったエリアもトルツメする。空なら全エリア許可。
 * - 日程カバレッジ（`dateRange`）: 範囲を1室で通しカバーできない部屋タイプも**除外せず**残し、
 *   `unavailableForRange: true` を付与する（一覧から消さず「満室」として表示するため）。
 *   dateRange が null（日程フィルタなし）なら常に false。
 *   ただし拠点内の全部屋タイプが `unavailableForRange: true` になった場合、その拠点自体は
 *   **除外**する（満室の拠点を一覧に残す意味がないため）。
 *
 * 注意: 施設 / 建築タイプ / 大型 / EV は backend（`roomTypesForReservation` の引数）で絞られ、
 * 除外された部屋タイプはそもそも入力（areas）に含まれないため、この関数の対象外。
 *
 * 型注釈上の注意: RT/S/A は相互参照する制約チェーン（`S extends SiteWithRoomTypes<RT>` 等）
 * のため、TS の型推論だけでは呼び出し側の実際の型（`id` 等の具象フィールド）まで辿れず、
 * 制約自体にフォールバックしてしまう。呼び出し側では明示的に型引数を渡すこと
 * （例: `selectVisibleAreas<BrowseRoomType, BrowseSite, BrowseArea>(...)`）。
 */
export function selectVisibleAreas<
  RT extends CoverageRoomType & { id: string },
  S extends SiteWithRoomTypes<RT>,
  A extends AreaWithSites<S> & { slug: string },
>(
  areas: readonly A[],
  filters: {
    dateRange: { firstNight: PlainDate; lastNight: PlainDate } | null;
    selectedAreaSlugs: ReadonlySet<string>;
  },
): AnnotatedArea<RT, S, A>[] {
  const { dateRange, selectedAreaSlugs } = filters;

  return areas.flatMap((area): AnnotatedArea<RT, S, A>[] => {
    const areaAllowed =
      selectedAreaSlugs.size === 0 || selectedAreaSlugs.has(area.slug);

    const sitesForReservation = area.sitesForReservation.flatMap(
      (site): AnnotatedArea<RT, S, A>["sitesForReservation"] => {
        const roomTypesForReservation = site.roomTypesForReservation.flatMap(
          (rt): (RT & { unavailableForRange: boolean })[] => {
            if (!areaAllowed) {
              return [];
            }
            const unavailableForRange =
              dateRange !== null &&
              !keepRoomTypeForRange(
                rt,
                dateRange.firstNight,
                dateRange.lastNight,
              );
            return [{ ...rt, unavailableForRange }];
          },
        );
        if (roomTypesForReservation.length === 0) {
          return [];
        }
        // dateRange が null の間は unavailableForRange が常に false なので、
        // ここは自然に発火しない（日程フィルタなし時に拠点を除外しない回帰要件と両立する）。
        if (roomTypesForReservation.every((rt) => rt.unavailableForRange)) {
          return [];
        }
        return [{ ...site, roomTypesForReservation }];
      },
    );

    if (sitesForReservation.length === 0) {
      return [];
    }
    return [{ ...area, sitesForReservation }];
  });
}

// ─── 在庫件数の集計（公開 API） ─────────────────────────────────────────────

/**
 * `selectVisibleAreas` の戻り値から、画面に表示された拠点数・部屋タイプ数を集計する。
 * GTM の在庫件数トラッキング（site_count/room_type_count）に使う。
 * `unavailableForRange: true`（満室として表示される）の部屋タイプは在庫として数えない。
 * 拠点は「1件でも空きがあれば残す」という `selectVisibleAreas` 側の除外ロジックにより
 * 既に満室拠点が取り除かれているため、素朴な件数のままでよい。
 */
export function countVisibleInventory(
  areas: readonly {
    sitesForReservation: readonly {
      roomTypesForReservation: readonly { unavailableForRange: boolean }[];
    }[];
  }[],
): { siteCount: number; roomTypeCount: number } {
  const sites = areas.flatMap((area) => area.sitesForReservation);
  const roomTypeCount = sites.reduce(
    (sum, site) =>
      sum +
      site.roomTypesForReservation.filter((rt) => !rt.unavailableForRange)
        .length,
    0,
  );
  return { siteCount: sites.length, roomTypeCount };
}
